/**
 * Remembers which board the user was last on, so a fresh load returns them
 * there instead of the first board in the list.
 *
 * The web counterpart of the mobile app's `prefs:last_board_id`
 * (src/ClientApp-Mobile/src/storage/preferences.ts). Kept here rather than in
 * @onetasklist/shared because the shared library must not touch `localStorage`.
 *
 * Every access is guarded: `localStorage` throws on access in some private
 * browsing modes, and is absent entirely when the bundle is evaluated without
 * a DOM.
 */

export const LAST_BOARD_STORAGE_KEY = "otl-last-board-id";

/** The last board the user opened, or `null` if none is remembered. */
export const getLastBoardId = (): string | null => {
  try {
    return window.localStorage.getItem(LAST_BOARD_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Records `boardId` as the board to return to on the next load. */
export const setLastBoardId = (boardId: string): void => {
  if (!boardId) {
    return;
  }

  try {
    window.localStorage.setItem(LAST_BOARD_STORAGE_KEY, boardId);
  } catch {
    // Storage is full or blocked — remembering the board is best-effort.
  }
};

/**
 * Picks the board to open from the user's boards: the remembered one when it
 * is still in the list, otherwise the first board.
 *
 * Boards the user no longer has (deleted, access revoked, or belonging to a
 * different region) fall back rather than routing to a board that 404s.
 */
export const resolveStartingBoardId = <TBoard extends { id: string }>(
  boards: readonly TBoard[],
  lastBoardId: string | null,
): string | undefined => {
  if (lastBoardId && boards.some((board) => board.id === lastBoardId)) {
    return lastBoardId;
  }

  return boards[0]?.id;
};
